import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from 'context/AuthProvider';

/**
 * ProtectedRoute Component
 * -------------------------------------------
 * This component serves as a security layer for routes that require authentication.
 * It wraps around routes that should only be accessible to logged-in users.
 * 
 * Key Features:
 * 1. Prevents unauthorized access to protected routes
 * 2. Redirects unauthenticated users to the login page
 * 3. Handles loading states to prevent UI flickering
 * 4. Uses React Router's Outlet pattern for nested route rendering
 * 
 * Usage Example:
 * <Route element={<ProtectedRoute />}>
 *   <Route path="admin/*" element={<AdminLayout />} />
 * </Route>
 */
const ProtectedRoute = () => {
  // Get authentication state and loading state from AuthContext
  const { isAuthenticated, authLoading } = useAuth();
  
  // If authentication check is still in progress, render nothing
  // This prevents momentary flashes of protected content or login redirects
  // while the auth state is being determined from token validation
  if (authLoading) {
    return null;
    // Note: You could also return a loading spinner here if desired
  }
  
  // If the user is not authenticated, redirect to the login page
  // The 'replace' prop replaces the current entry in the history stack
  // This prevents users from using the back button to access protected pages
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }
  
  // If the user is authenticated, render the nested routes using Outlet
  // Outlet will render whatever child route matches the current URL
  return <Outlet />;
};

export default ProtectedRoute;
